import React, { useState, useEffect } from 'react';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../AppContext';
import app from '../firebase-config'; // Correctly import the app instance
import DreamWeaveNeuralNetwork from '../components/DreamWeave/DreamWeaveNeuralNetwork';
import useDreamWeaveData from '../hooks/useDreamWeaveData';
import WhisperPolls from '../components/DreamWeave/WhisperPolls';
import StarlightBids from '../components/DreamWeave/StarlightBids';
import AuraPulse from '../components/DreamWeave/AuraPulse';
import DreamWeaveInfo from '../components/DreamWeave/DreamWeaveInfo';
import LoadingSpinner from '../components/LoadingSpinner';
import * as LucideIcons from 'lucide-react';
import TacticalSort from '../components/DreamWeave/TacticalSort';
import LogicChain from '../components/DreamWeave/LogicChain';
import RiskMatrix from '../components/DreamWeave/RiskMatrix';
import DreamWeaveEvents from '../components/DreamWeave/DreamWeaveEvents';
import DreamWeaving from '../components/DreamWeave/DreamWeaving';
import AuraEater from '../components/DreamWeave/games/aura-eater/AuraEater';
import SynapticStacking from '../components/DreamWeave/SynapticStacking';
import SynapticSerpent from '../components/DreamWeave/games/synaptic-serpent';
import CosmicAscent from '../components/DreamWeave/CosmicAscent';
import StarlightSentinel from '../components/DreamWeave/games/starlight-sentinel/StarlightSentinel';
import GameViewer from '../components/DreamWeave/GameViewer';
import AISpectatorPage from './AISpectatorPage';
import DreamWeaveVisualization from '../components/DreamWeave/DreamWeaveVisualization';

import ShowdownPage from './ShowdownPage';

import { gameComponents } from '../gameComponents';

const TabButton = ({ title, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={`px-5 py-2 text-lg font-semibold transition-all duration-300 ${isActive ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
    >
        {title}
    </button>
);

const ContributionMethodButton = ({ title, description, onSelect, icon }) => {
    const Icon = LucideIcons[icon];
    return (
        <button
            onClick={onSelect}
            className="flex flex-col items-center justify-center text-center p-6 border border-gray-700 rounded-xl bg-gray-800 text-white cursor-pointer transition-all duration-300 shadow-lg hover:-translate-y-1 hover:shadow-xl hover:shadow-indigo-500/20 hover:border-indigo-500 h-full"
        >
            <Icon className="w-10 h-10 text-indigo-400 mb-3" />
            <h4 className="font-bold text-indigo-300 text-lg">{title}</h4>
            <p className="text-xs text-gray-400 mt-1 flex-grow">{description}</p>
        </button>
    );
};

function DreamWeavePage() {
    const { t } = useTranslation();
    const dreamWeaveId = 'globalDreamWeave'; 
    const nexusId = null; 

    const { neurons, synapses, quests, event, constellations, loading, error } = useDreamWeaveData(dreamWeaveId, nexusId);
    const [activeView, setActiveView] = useState('network');
    const [selectedArcadeGame, setSelectedArcadeGame] = useState(null);

    useEffect(() => {
        if (activeView !== 'contribute') setSelectedArcadeGame(null);
    }, [activeView]);

    if (loading) {
        return (
            <div className="w-screen h-screen bg-black flex justify-center items-center">
                <LoadingSpinner />
                <p className="text-white ml-4">{t('dreamWeave.loading')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-screen h-screen bg-black flex justify-center items-center text-red-500">
                {t('dreamWeave.error', { message: error.message })}
            </div>
        );
    }

    const renderActiveView = () => {
        const SelectedGameComponent = selectedArcadeGame ? gameComponents[selectedArcadeGame] : null;

        switch (activeView) {
            case 'network':
                return <DreamWeaveVisualization />;
            case 'contribute':
                return (
                    <div className="p-4 md:p-8 h-full overflow-y-auto bg-gray-900 text-white">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl font-bold text-center mb-8 text-white">{t('dreamWeave.contribute.title')}</h2>
                            <p className="text-center text-gray-400 mb-8">{t('dreamWeave.contribute.description')}</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <ContributionMethodButton icon="Flame" title={t('dreamWeave.contribute.auraEater.title')} description={t('dreamWeave.contribute.auraEater.description')} onSelect={() => setSelectedArcadeGame('auraEater')} />
                                <ContributionMethodButton icon="Layers" title={t('dreamWeave.contribute.synapticStacking.title')} description={t('dreamWeave.contribute.synapticStacking.description')} onSelect={() => setSelectedArcadeGame('synapticStacking')} />
                                <ContributionMethodButton icon="Spline" title={t('dreamWeave.contribute.synapticSerpent.title')} description={t('dreamWeave.contribute.synapticSerpent.description')} onSelect={() => setSelectedArcadeGame('synapticSerpent')} />
                                <ContributionMethodButton icon="Rocket" title={t('dreamWeave.contribute.cosmicAscent.title')} description={t('dreamWeave.contribute.cosmicAscent.description')} onSelect={() => setSelectedArcadeGame('cosmicAscent')} />
                                <ContributionMethodButton icon="Shield" title={t('dreamWeave.contribute.starlightSentinel.title')} description={t('dreamWeave.contribute.starlightSentinel.description')} onSelect={() => setSelectedArcadeGame('starlightSentinel')} />
                            </div>
                        </div>
                        {SelectedGameComponent && (
                            <GameViewer
                                dreamWeaveId={dreamWeaveId}
                                nexusId={nexusId}
                                onClose={() => setSelectedArcadeGame(null)}
                                gameComponent={<SelectedGameComponent />}
                            />
                        )}
                    </div>
                );
            case 'aiSpectator':
                return <AISpectatorPage />;
            case 'showdown':
                return <ShowdownPage />;
            case 'info':
                return (
                    <div className="p-4 md:p-8 h-full overflow-y-auto bg-gray-900 text-white custom-scrollbar">
                        <DreamWeaveInfo setActiveTab={setActiveView} />
                    </div>
                );
            default:
                return <DreamWeaveNeuralNetwork neurons={neurons} synapses={synapses} constellations={constellations} />;
        }
    };

    return (
        <div className="w-full h-full relative">
            <DreamWeaveEvents quests={quests} event={event} setActiveTab={setActiveView} />
            <div className="p-2 bg-gray-900 border-b border-gray-700 text-center">
                <TabButton title={t('dreamWeave.tabs.visualization')} isActive={activeView === 'network'} onClick={() => setActiveView('network')} />
                <TabButton title={t('dreamWeave.tabs.contribute')} isActive={activeView === 'contribute'} onClick={() => setActiveView('contribute')} />
                <TabButton title={t('dreamWeave.tabs.aiSpectator')} isActive={activeView === 'aiSpectator'} onClick={() => setActiveView('aiSpectator')} />
                <TabButton title={t('dreamWeave.tabs.showdown')} isActive={activeView === 'showdown'} onClick={() => setActiveView('showdown')} />
                <TabButton title={t('dreamWeave.tabs.info')} isActive={activeView === 'info'} onClick={() => setActiveView('info')} />
            </div>
            <div className="flex-1 relative">
                {renderActiveView()}
            </div>
        </div>
    );
}

export default DreamWeavePage;